<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.showPreviewThumbs') }}
      </span>
    </v-col>

    <v-row no-gutters class="px-2">
      <v-col cols="12">
        <p>
          {{ $t('message.pages.video.messageShowPreviewThumbs') }}
        </p>
        <v-alert
          v-if="hasAlternativeProgress"
          class="mt-1"
          border="left"
          colored-border
          type="warning"
          elevation="1"
          icon="mdi-alert"
        >
          <span
            style="font-size: 14px"
            v-html="
              $t(
                'message.pages.video.messageShowPreviewThumbsAlertAlternativeProgress'
              )
            "
          ></span>
        </v-alert>
        <v-alert
          v-if="!hasAlternativeProgress && !hasProgressBar"
          class="mt-1"
          border="left"
          colored-border
          type="warning"
          elevation="1"
          icon="mdi-alert"
        >
          <span
            style="font-size: 14px"
            v-html="
              $t('message.pages.video.messageShowPreviewThumbsAlertProgressBar')
            "
          ></span>
        </v-alert>
        <v-row class="mt-3">
          <v-col cols="6" class="py-0">
            <v-switch
              v-model="showPreviewThumbs"
              class="mt-0"
              hide-details
              :disabled="
                hasAlternativeProgress || !hasProgressBar || !hasEditVideoPerm
              "
              :label="$t('message.pages.video.ative')"
              @click="changePlayer"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addAlternativeProgress') }}
    </span>

    <v-row no-gutters class="pt-5" v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-0 pt-4 pb-6">
          <v-spacer />
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            color="primary"
            class="px-6"
            :disabled="loadingButtonSave"
            :loading="loadingButtonSave"
            @click="handleUpdate"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import i18n from '@/translations/lang'

export default {
  name: 'VideoPlayerPreviewThumbs',
  components: {
    UnsavedChangesModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    videoId: '',
    loadingButtonSave: false,
    profileId: null,
    initialConfig: {},
    showPreviewThumbs: false,
    withoutChanges: true,
    alternativeProgress: false,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ?? this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.alternativeProgress =
      this.$store.state.video.profile?.vsl.alternativeProgress
    this.showPreviewThumbs =
      this.$store.state.video.profile?.general?.showPreviewThumbs ?? true
    this.initialConfig.showPreviewThumbs = _.cloneDeep(this.showPreviewThumbs)
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    hasAlternativeProgress() {
      return this.alternativeProgress
    },
    hasProgressBar() {
      return this.$store.state.video.profile.controls.includes('progress')
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO_PROFILE', 'UPDATE_STATUS_ERROR']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleUpdate() {
      const general = this.$store.state.video.profile?.general

      this.loadingButtonSave = true

      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }

        await this.updateProfile([this.profileId, { general }])

        if (this.internalError) return

        this.handleSnackbar(i18n.t('message.pages.video.changesSaved'))
        this.initialConfig.showPreviewThumbs = _.cloneDeep(
          this.showPreviewThumbs
        )
        this.withoutChanges = true
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    changePlayer() {
      this.profile.general.showPreviewThumbs = this.showPreviewThumbs

      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleUndoChanges() {
      this.showPreviewThumbs = _.cloneDeep(this.initialConfig.showPreviewThumbs)
      this.changePlayer()
    },
    checkChanges() {
      return (
        this.profile.general.showPreviewThumbs ===
        this.initialConfig.showPreviewThumbs
      )
    },
    beforeLeave() {
      if (!this.checkChanges()) return this.$root.$emit('unsavedChangesModal')

      this.UPDATE_STATUS_ERROR(false)
      this.handleBack()
    },
    handleSnackbar(item) {
      const text = item || i18n.t('message.changesSaved')
      const open = true

      this.UPDATE_GLOBAL_SNACKBAR({ open, text })
    },
  },
}
</script>
